import { memo, useCallback, useState } from 'react';
import { ModalConversionPoints } from '@/components/Modals/shared-types';
import UpgradeChoiceModal from '@/components/Modals/UpgradeChoice';
import Popover from '@/components/Popover';
import { useWhitelistedChannels } from '@/components/UBSafelist/hooks/useWhitelistedChannels';
import { useHasRole, useIsLoggedIn, useSubscriptionType } from '@/hooks/auth';
import { useUpsellModal } from '@/hooks/billing/useUpsellModal';
import { getPlanHierarchyLevel } from '@/lib/plan-hierarchy';
import { cn } from '@/lib/utils';
import SafelistButton from './SafelistButton';
import SafelistContent from './SafelistContent';
import { buttons, ButtonType } from './types';
const SafelistYourChannel = () => {
  const userIsLoggedIn = useIsLoggedIn();
  const isFree = useHasRole('Free');
  const subscriptionType = useSubscriptionType();
  const {
    openUpsell
  } = useUpsellModal();
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const {
    data: whitelistedChannels,
    isInitialLoading
  } = useWhitelistedChannels();
  const hasSafelistedChannels = whitelistedChannels && whitelistedChannels.length > 0;

  // Anything below Creator gains channels by moving to Creator; Creator itself
  // has to reach Pro. Pro and Business never see the prompt at all.
  const upgradeTarget = getPlanHierarchyLevel(subscriptionType) < getPlanHierarchyLevel('Creator') ? 'Creator' : 'Pro';
  const handleUpgrade = useCallback(() => {
    // Free has no subscription to prorate against, so it keeps the Subscribe
    // modal rather than the chooser (which only offers in-app plan changes).
    if (isFree) {
      openUpsell({
        targetPlan: upgradeTarget,
        conversionPoint: ModalConversionPoints.Header
      });
      return;
    }
    setUpgradeOpen(true);
  }, [isFree, openUpsell, upgradeTarget]);

  // Hide the safelist button if the user is not logged in
  if (!userIsLoggedIn) {
    return null;
  }
  let button = buttons[ButtonType.Button];
  if (isInitialLoading) {
    button = buttons[ButtonType.Skeleton];
  } else if (hasSafelistedChannels) {
    button = buttons[ButtonType.Safelist];
  }
  return <div className="flex h-full flex-1 items-center" data-sentry-component="SafelistYourChannel" data-sentry-source-file="index.tsx">
      {/* The mobile drawer sizes to its content, so an uncapped channel list
          grows past the viewport and Vaul treats scroll gestures as drawer
          drags: the list snaps down or dismisses on release (DEV-6676). The
          cap keeps the list scrollable and fully reachable instead. */}
      <Popover.Container animateOverTrigger className="w-[unset] px-0 pb-0 max-xs:max-h-[90dvh] md:max-w-[320px] 2xl:max-w-[340px]" data-sentry-element="Popover.Container" data-sentry-source-file="index.tsx">
        <Popover.Trigger tooltip={{
        label: button.tooltip
      }} data-sentry-element="Popover.Trigger" data-sentry-source-file="index.tsx">
          <SafelistButton className={cn(isFree ? 'xl:flex! xs:flex hidden md:hidden' : 'flex')} data-testid="safelist-button" safelistButtonType={button.type} data-sentry-element="SafelistButton" data-sentry-source-file="index.tsx" />
        </Popover.Trigger>
        <Popover.Content data-sentry-element="Popover.Content" data-sentry-source-file="index.tsx">
          {/* Vaul pins a pixel height on the mobile drawer while the keyboard is open; without a scroll cap the channel search grows past it and is clipped off-screen (DEV-6674). */}
          <SafelistContent className="max-h-[90dvh] w-full overflow-y-auto" onUpgrade={handleUpgrade} data-sentry-element="SafelistContent" data-sentry-source-file="index.tsx" />
        </Popover.Content>
      </Popover.Container>
      {/* Sibling of the popover on purpose: rendered inside it, the chooser
          would be unmounted by the drawer dismiss before it could open. */}
      <UpgradeChoiceModal conversionPoint={ModalConversionPoints.Header} minimumPlan={upgradeTarget} onOpenChange={setUpgradeOpen} open={upgradeOpen} title="Upgrade to safelist more channels" data-sentry-element="UpgradeChoiceModal" data-sentry-source-file="index.tsx" />
    </div>;
};
export default memo(SafelistYourChannel);